import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import HistoryMap from "@/components/HistoryMap";
import { money, fmtDay } from "@/lib/format";
import { downloadCsv } from "@/lib/download";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Layers, MapPin, Download, ChevronLeft, AlertTriangle, MoreVertical, Users, X } from "lucide-react";

const RANGES = [
  { key: "7", label: "This week", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
  { key: "all", label: "All time", days: 3650 },
];

function isoDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
const fmtTime = (t) => t ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

// merge consecutive check-ins at the same garage into one entry with a time range
function collapseStops(stops) {
  const out = [];
  for (const s of stops) {
    const last = out[out.length - 1];
    if (last && last.garage_id === s.garage_id) {
      last.endTime = s.time; last.count += 1;
      if (s.note) last.notes.push(s.note);
      if (s.outcome) last.outcome = s.outcome;
    } else {
      out.push({ ...s, endTime: s.time, count: 1, notes: s.note ? [s.note] : [] });
    }
  }
  return out.map((s, i) => ({ ...s, order: i + 1 }));
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-center">
      <div className="text-xl font-black text-[#111827] leading-tight">{value}</div>
      <div className="text-[10px] font-bold uppercase text-[#6B7280]">{label}</div>
      {sub && <div className="text-[9px] text-[#9CA3AF] uppercase">{sub}</div>}
    </div>
  );
}

export default function HistoryPanel({ isManager, salesEmail, onOpenDetail }) {
  const [range, setRange] = useState("30");
  const [by, setBy] = useState("");
  const [salesmen, setSalesmen] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);
  const [selected, setSelected] = useState({});
  const [overlay, setOverlay] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [homeBase, setHomeBase] = useState(null);

  const params = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    const p = { days_from: isoDaysAgo(r.days) };
    if (isManager && by) p.by = by;
    return p;
  }, [range, by, isManager]);

  useEffect(() => { api.get("/settings/home-base").then((r) => setHomeBase(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isManager) api.get("/auth/users").then((r) => setSalesmen(r.data.filter((u) => u.role === "salesman"))).catch(() => {}); }, [isManager]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/history/days", { params }), api.get("/history/coverage", { params })]);
      setDays(d.data); setCoverage(c.data); setSelected({});
    } catch { toast.error("Could not load history"); }
    setLoading(false);
  }, [params]);
  useEffect(() => { load(); }, [load]);

  // one row per DAY, both people's activity inside it
  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of days) {
      if (!m.has(r.date)) m.set(r.date, { date: r.date, visits: 0, garages: 0, order_count: 0, order_value: 0, people: new Set(), areas: new Set() });
      const g = m.get(r.date);
      g.visits += r.visits; g.order_count += r.order_count; g.order_value += r.order_value; g.garages += r.garages;
      if (r.by) g.people.add(r.by);
      (r.areas || []).forEach((a) => g.areas.add(a));
    }
    return Array.from(m.values()).map((g) => ({ ...g, people: Array.from(g.people), areas: Array.from(g.areas) })).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [days]);

  const openDayDetail = async (row) => {
    setOpenDay({ ...row, data: null });
    try {
      const { data } = await api.get("/history/day", { params: { date: row.date, ...(isManager && by ? { by } : {}) } });
      setOpenDay({ ...row, data });
    } catch { toast.error("Could not open day"); }
  };

  const toggleSel = (date) => setSelected((s) => ({ ...s, [date]: !s[date] }));
  const selDates = Object.keys(selected).filter((k) => selected[k]);
  const buildOverlay = async () => {
    if (selDates.length === 0) return;
    try {
      const { data } = await api.get("/history/overlay", { params: { dates: selDates.join(","), ...(isManager && by ? { by } : {}) } });
      setOverlay(data);
    } catch { toast.error("Could not build overlay"); }
  };

  if (openDay) {
    const d = openDay.data;
    const stops = d ? collapseStops(d.stops) : [];
    return (
      <div data-testid="history-day-detail" className="pb-4">
        <button onClick={() => setOpenDay(null)} className="text-sm font-bold text-[#1E3A8A] flex items-center gap-1 mb-3" data-testid="history-back"><ChevronLeft className="w-4 h-4" /> Back to days</button>
        <h1 className="text-2xl font-black text-[#1E3A8A]">{fmtDay(openDay.date)}</h1>
        <p className="text-sm text-[#6B7280] mb-3 flex items-center gap-1.5"><Users className="w-4 h-4" /> {openDay.people.join(", ") || "—"}</p>
        {!d ? <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div> : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4" data-testid="day-stats">
              <Stat label="Visits" value={openDay.visits} />
              <Stat label={openDay.order_count === 1 ? "Order" : "Orders"} value={openDay.order_count} />
              <Stat label="Value" value={money(openDay.order_value)} />
              <Stat label="Distance" value={d.km != null ? `${d.km} km` : "—"} sub="est." />
              <Stat label="On road" value={d.hours != null ? `${d.hours} h` : "—"} sub="est." />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-5 order-2 lg:order-1 space-y-2 max-h-[55vh] overflow-y-auto" data-testid="history-timeline">
                {stops.map((s) => (
                  <div key={s.order} onClick={() => onOpenDetail(s.garage_id)} className="border border-[#E5E7EB] rounded-lg p-2.5 cursor-pointer hover:border-[#93C5FD]" data-testid={`history-stop-${s.order}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-xs font-black flex items-center justify-center shrink-0">{s.order}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm text-[#111827] truncate">{s.name}</div>
                        <div className="text-xs text-[#6B7280] truncate">
                          {s.area} · {fmtTime(s.time)}{s.count > 1 ? `–${fmtTime(s.endTime)}` : ""}
                          {s.count > 1 && <span className="ml-1 text-[#B45309] font-bold">×{s.count}</span>}
                        </div>
                      </div>
                    </div>
                    {s.notes.length > 0 && <div className="text-xs text-[#374151] mt-1 pl-8">{s.notes.join(" · ")}</div>}
                  </div>
                ))}
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <HistoryMap days={[{ date: d.date, geometry: d.geometry, points: stops.map((s) => ({ lat: s.latitude, lng: s.longitude, order: s.order, name: s.name })) }]} homeBase={homeBase} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="history-panel" className="pb-24">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">History</h1>
          <p className="text-sm text-[#6B7280] mt-1">Past working days — routes, coverage and blind spots.</p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="history-menu" className="h-11 w-11 shrink-0 rounded-lg border border-[#D1D5DB] bg-white flex items-center justify-center text-[#6B7280]"><MoreVertical className="w-5 h-5" /></button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-1">
            <button onClick={() => downloadCsv("/export/garages.csv", "jam-garages.csv")} data-testid="history-export"
              className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-[#374151] hover:bg-[#F9FAFB]"><Download className="w-4 h-4" /> Export garages CSV</button>
          </PopoverContent>
        </Popover>
      </div>

      {/* period + person on one row */}
      <div className="flex items-center gap-2 mb-4">
        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="history-range"
          className="h-11 px-3 rounded-lg border border-[#D1D5DB] bg-white text-sm font-bold flex-1 min-w-0">
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {isManager && (
          <select value={by} onChange={(e) => setBy(e.target.value)} data-testid="history-by"
            className="h-11 px-3 rounded-lg border border-[#D1D5DB] bg-white text-sm flex-1 min-w-0">
            <option value="">Everyone</option>
            {salesmen.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
          </select>
        )}
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            {grouped.length === 0 ? <p className="text-sm text-[#9CA3AF] py-6">No visits recorded in this range.</p> : (
              <div className="space-y-2">
                {grouped.map((r) => (
                  <div key={r.date} className={`bg-white border rounded-lg p-3 flex items-center gap-3 ${selected[r.date] ? "border-[#1E3A8A] ring-1 ring-[#1E3A8A]" : "border-[#E5E7EB]"}`} data-testid={`history-day-${r.date}`}>
                    <input type="checkbox" checked={!!selected[r.date]} onChange={() => toggleSel(r.date)} data-testid={`select-day-${r.date}`} className="w-5 h-5 accent-[#1E3A8A]" />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDayDetail(r)}>
                      <div className="font-bold text-[#111827]">{fmtDay(r.date)} <span className="text-xs font-normal text-[#6B7280]">· {r.people.join(", ")}</span></div>
                      <div className="text-xs text-[#6B7280] truncate">{r.visits} visits · {r.order_count} {r.order_count === 1 ? "order" : "orders"} · {money(r.order_value)} · {r.areas.join(", ") || "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            {overlay && (
              <div className="mb-4" data-testid="overlay-map">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold uppercase text-[#6B7280]">{overlay.length} days overlaid</h3>
                  <button onClick={() => setOverlay(null)} className="text-xs font-bold text-[#6B7280]">Clear</button>
                </div>
                <HistoryMap days={overlay} homeBase={homeBase} heightClass="h-[40vh] min-h-[280px]" testId="overlay-history-map" />
              </div>
            )}
            <h3 className="text-sm font-bold uppercase text-[#6B7280] mb-2 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Coverage by area</h3>
            <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="coverage-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-xs uppercase text-[#6B7280]">
                  <tr><th className="text-left px-3 py-2">Area</th><th className="px-3 py-2">Visits</th><th className="px-3 py-2">Customers</th><th className="px-3 py-2">Unvisited</th></tr>
                </thead>
                <tbody>
                  {coverage.map((c) => (
                    <tr key={c.area} className={`border-t border-[#F3F4F6] ${c.blind_spot ? "bg-[#FEF3C7]" : ""}`} data-testid={`coverage-${c.area}`}>
                      <td className="px-3 py-2 font-medium text-[#111827]">{c.blind_spot && <AlertTriangle className="w-3.5 h-3.5 text-[#B45309] inline mr-1" />}{c.area}</td>
                      <td className="px-3 py-2 text-center">{c.visits}</td>
                      <td className="px-3 py-2 text-center">{c.customers}</td>
                      <td className="px-3 py-2 text-center font-bold">{c.prospects_unvisited}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-2 flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[#FEF3C7] border border-[#FCD34D]" /> Highlighted = blind spot: barely visited but prospects waiting.</p>
          </div>
        </div>
      )}

      {selDates.length > 0 && (
        <div className="fixed left-3 right-3 bottom-20 md:bottom-6 z-[500] bg-[#1E3A8A] text-white rounded-xl shadow-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="history-selection-bar">
          <span className="text-sm font-bold">{selDates.length} day{selDates.length !== 1 ? "s" : ""} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected({})} className="h-10 px-3 rounded-lg bg-white/15 font-bold text-sm flex items-center gap-1"><X className="w-4 h-4" /> Clear</button>
            <button onClick={buildOverlay} data-testid="build-overlay" className="h-10 px-3 rounded-lg bg-white text-[#1E3A8A] font-bold text-sm flex items-center gap-1.5"><Layers className="w-4 h-4" /> Overlay</button>
          </div>
        </div>
      )}
    </div>
  );
}
